<template>
  <v-card flat border class="ma-4">
    <!-- Title -->
    <v-card-item>
      <template v-slot:title>
        {{ $t("tenant.contacts.title") }}
      </template>
      <template v-slot:append>
        <v-tooltip location="top" v-if="canAddContact">
          <template v-slot:activator="{ props }">
            <span v-bind="props">
              <v-icon :disabled="showEditDialog" @click="onAddContact()"
                >mdi-plus-circle
              </v-icon>
            </span>
          </template>
          <span>{{ $t("tenant.contacts.actions.addContact") }}</span>
        </v-tooltip>
      </template>
    </v-card-item>

    <v-card-text class="pb-0">
      <!-- Table -->
      <v-row>
        <v-col cols="12">
          <v-data-table :headers="headers" :items="contacts">
            <template v-slot:[`item.action`]="{ item }">
              <v-icon size="small" class="mr-2" @click="onEditContact(item)">
                mdi-pencil
              </v-icon>
              <v-icon size="small" @click="onRemoveClick(item)">
                mdi-delete
              </v-icon>
            </template>
            <template v-slot:[`item.type`]="{ item }">
              {{ $t("tenant.contacts.contactTypes." + item.type) }}
            </template>
          </v-data-table>
        </v-col>
      </v-row>

      <!-- Error message -->
      <v-row>
        <v-col cols="12" class="py-0">
          <v-alert
            v-if="error"
            :type="error.type"
            class="ma-0"
            elevation="2"
            border="start"
            density="compact"
            closable
          >
            <div>{{ error.message }}</div>
            <div v-if="error.causedBy" class="subtitle">
              {{ error.causedBy }}
            </div>
          </v-alert>
        </v-col>
      </v-row>
    </v-card-text>

    <v-snackbar
      v-model="snackbar"
      :timeout="2000"
      location="top"
      theme="dark"
      color="green-darken-1"
    >
      {{ snackbarText }}

      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar = false">
          {{ $t("common.actions.close") }}
        </v-btn>
      </template>
    </v-snackbar>

    <ConfirmRemoveDialog
      :itemName="`${$t('tenant.contacts.title')}`"
      confirmRemoveEvent="confirmEvent"
      :show="showConfirmRemove"
      @close="showConfirmRemove = false"
      v-on:confirmEvent="onRemoveConfirm"
    />
    <EditTenantContactDialog
      :editedItem="editedItem"
      :show="showEditDialog"
      @save="onEditSave"
      @cancel="onEditCancel"
    />
  </v-card>
</template>

<script>
import ConfirmRemoveDialog from "@/dialogs/ConfirmRemoveDialog"
import EditTenantContactDialog from "@/dialogs/EditTenantContactDialog"
import fetchClient from "@/utils/fetchClient"

export default {
  data() {
    return {
      headers: [
        {
          title: this.$t("tenant.contacts.dataTable.headers.name"),
          sortable: true,
          key: "name"
        },
        {
          title: this.$t("tenant.contacts.dataTable.headers.type"),
          sortable: true,
          key: "type"
        },
        {
          title: this.$t("tenant.contacts.dataTable.headers.phoneNumber"),
          sortable: true,
          key: "phoneNumber"
        },
        {
          title: this.$t("tenant.contacts.dataTable.headers.address"),
          sortable: true,
          key: "address"
        },
        {
          title: this.$t("common.dataTable.headers.actions"),
          align: "right",
          sortable: false,
          key: "action"
        }
      ],

      contacts: [],
      showEditDialog: false,
      editedItem: undefined,
      showConfirmRemove: false,
      error: null,
      snackbar: false,
      snackbarText: ""
    }
  },
  components: {
    ConfirmRemoveDialog,
    EditTenantContactDialog
  },
  mounted() {
    this.fetchContacts()
  },
  computed: {
    canAddContact() {
      return this.$store.getters.canEditTenants
    },
  },
  methods: {
    async fetchContacts() {
      try {
        await this.$store.dispatch("fetchTenantContacts", {
          tenantUid: this.$route.params.tenantUid
        })

        this.contacts = this.$store.getters.tenantContacts
      } catch (error) {
        this.error = { type: "error", ...error }
        this.contacts = []
      }
    },
    onEditContact(contact = null) {
      this.editedItem = contact
      this.showEditDialog = true
    },
    onAddContact() {
      this.onEditContact({
        id: null,
        name: "",
        type: "",
        phoneNumber: "",
        address: ""
      })
    },
    async onEditSave(info) {
      this.showEditDialog = false
      this.snackbarText = info.isNewItem
        ? this.$t("tenant.contacts.messages.contactAdded")
        : this.$t("tenant.contacts.messages.contactUpdated")
      this.snackbar = true
      await this.fetchContacts()
    },
    onEditCancel() {
      this.showEditDialog = false
    },
    onRemoveClick(contact) {
      this.editedItem = contact
      this.showConfirmRemove = true
    },
    async onRemoveConfirm() {
      this.isUpdating = true
      this.error = null

      try {
        // TODO move to store
        const url = `/api/v1/tenants/${this.$route.params.tenantUid}/contacts/${this.editedItem.uid}`
        await fetchClient().deleteOrThrow(url)
        this.snackbarText = this.$t("tenant.contacts.messages.removed")
        this.snackbar = true
        await this.fetchContacts()
        this.isUpdating = false
      } catch (error) {
        console.log(error)
        this.error = { type: "error", ...error }
      } finally {
        this.isUpdating = false
      }
    }
  }
}
</script>

<style></style>
